import { useProjectStore } from '../../store/useProjectStore';
import { useSSEStore, type SkillCardData } from '../../store/useSSEStore';
import { useReviewStore } from '../../store/useReviewStore';
import { useAuthStore } from '../../store/useAuthStore';
import { TeamPanel } from '../team/TeamPanel';
import * as api from '../../api/endpoints';
import * as Sentry from '@sentry/react';
import { useState, useEffect, useCallback } from 'react';
import type { TabId } from '../../types';

const ALL_TABS: { id: TabId; label: string; ownerOnly?: boolean }[] = [
  { id: 'pipeline', label: 'Pipeline', ownerOnly: true },
  { id: 'extraction', label: 'Extraction' },
  { id: 'annotation', label: 'Annotation' },
  { id: 'review', label: 'Review' },
  { id: 'boq', label: 'Client BoQ' },
  { id: 'research', label: 'Research' },
  { id: 'cables', label: 'Cables', ownerOnly: true },
  { id: 'upload', label: 'Upload', ownerOnly: true },
];

// All skills in pipeline order for progress calculation
const ALL_SKILLS = [
  'pre_scan', 'triage', 'separation', 'boq_extraction', 'research_report',
  'diagram_extraction', 'floor_plan_annotation',
];

const RESEARCH_SKILLS = new Set(['pre_scan', 'triage', 'separation', 'boq_extraction', 'research_report']);
const EXTRACTION_SKILLS = new Set(['diagram_extraction', 'floor_plan_annotation']);

type PipelinePhase = 'research' | 'waiting' | 'extraction' | 'complete';

function computePipelineState(statuses: Record<string, SkillCardData>): { phase: PipelinePhase; progress: number } | null {
  const entries = Object.values(statuses);
  if (entries.length === 0) return null;

  let done = 0;
  for (const skill of ALL_SKILLS) {
    const s = statuses[skill];
    if (s && (s.status === 'success' || s.status === 'failed' || s.status === 'skipped')) done++;
  }

  const progress = Math.round((done / ALL_SKILLS.length) * 100);

  const hasRunning = entries.some((s) => s.status === 'running');
  const researchDone = [...RESEARCH_SKILLS].every((sk) => {
    const s = statuses[sk];
    return s && (s.status === 'success' || s.status === 'failed' || s.status === 'skipped');
  });
  const extractionDone = [...EXTRACTION_SKILLS].every((sk) => {
    const s = statuses[sk];
    return s && (s.status === 'success' || s.status === 'failed' || s.status === 'skipped');
  });

  if (researchDone && extractionDone) return { phase: 'complete', progress: 100 };
  if (researchDone && !hasRunning) return { phase: 'waiting', progress };
  if (researchDone) return { phase: 'extraction', progress };
  return { phase: 'research', progress };
}

type TabState = 'running' | 'ready' | undefined;

function computeTabStates(statuses: Record<string, SkillCardData>, projStore: {
  researchReport: unknown; extraction: unknown;
  floorPlanAnnotation: unknown;
  reviewData: unknown;
}): Partial<Record<TabId, TabState>> {
  const result: Partial<Record<TabId, TabState>> = {};

  // Research tab
  const researchRunning = ['pre_scan', 'triage', 'separation', 'boq_extraction', 'research_report'].some(
    (sk) => statuses[sk]?.status === 'running',
  );
  if (researchRunning) result.research = 'running';
  else if (projStore.researchReport) result.research = 'ready';

  // Extraction tab
  const extractionRunning = statuses['diagram_extraction']?.status === 'running';
  if (extractionRunning) result.extraction = 'running';
  else if (projStore.extraction) result.extraction = 'ready';

  // Annotation tab
  const annotationRunning = statuses['floor_plan_annotation']?.status === 'running';
  if (annotationRunning) result.annotation = 'running';
  else if (projStore.floorPlanAnnotation) result.annotation = 'ready';

  // Review tab
  const reviewPreparing = useReviewStore.getState().preparing;
  const reviewPublishing = useReviewStore.getState().publishing;
  if (reviewPreparing || reviewPublishing) result.review = 'running';
  else if (projStore.reviewData) result.review = 'ready';

  // Pipeline tab
  const anyRunning = Object.values(statuses).some((s) => s.status === 'running');
  if (anyRunning) result.pipeline = 'running';

  return result;
}

export function Header({
  onTabChange,
  onNewProject,
}: {
  onTabChange?: (tab: TabId) => void;
  onNewProject?: () => void;
}) {
  const project = useProjectStore((s) => s.project);
  const activeTab = useProjectStore((s) => s.activeTab);
  const tabAttention = useProjectStore((s) => s.tabAttention);
  const researchReport = useProjectStore((s) => s.researchReport);
  const extraction = useProjectStore((s) => s.extraction);
  const floorPlanAnnotation = useProjectStore((s) => s.floorPlanAnnotation);
  const reviewData = useReviewStore((s) => s.reviewData);
  const storeSetActiveTab = useProjectStore((s) => s.setActiveTab);
  const setActiveTab = onTabChange ?? storeSetActiveTab;
  const skillStatuses = useSSEStore((s) => s.skillStatuses);
  const authUser = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const isOwner = authUser?.role === 'owner';
  const tabs = ALL_TABS.filter((t) => !t.ownerOnly || isOwner);
  const [showTeam, setShowTeam] = useState(false);

  const pipelineState = computePipelineState(skillStatuses);
  const tabStates = computeTabStates(skillStatuses, { researchReport, extraction, floorPlanAnnotation, reviewData });

  return (
    <>
      <header>
        <div className="header-left">
          <h1
            className={onNewProject ? 'clickable' : ''}
            onClick={onNewProject}
            title={onNewProject ? 'Back to dashboard' : undefined}
          >MONCO</h1>
          {(project?.project_name || project?.name) && (
            <span className="project-name">{project.project_name || project.name}</span>
          )}
          <ProjectIdBadge />
        </div>
        <nav className="tabs">
          {tabs.map((t) => {
            const state = tabStates[t.id];
            const hasAttention = tabAttention[t.id];
            const cls = [
              'tab',
              activeTab === t.id ? 'active' : '',
              hasAttention ? 'attention' : '',
              !hasAttention && state === 'running' ? 'tab-running' : '',
            ].filter(Boolean).join(' ');

            return (
              <button
                key={t.id}
                className={cls}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
                {!hasAttention && state === 'ready' && <span className="tab-check" />}
              </button>
            );
          })}
        </nav>
        <div className="header-right">
          {authUser && (
            <span className="user-badge" title={`${authUser.username} (${authUser.role})`}>
              <span className="user-badge-name">{authUser.username}</span>
              <span className={`user-badge-role role-${authUser.role}`}>{authUser.role}</span>
              <button className="btn-logout" onClick={logout} title="Logout">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10.5 11.5L14 8l-3.5-3.5M5.5 8H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </span>
          )}
          {isOwner && project && (
            <button className="btn-team" onClick={() => setShowTeam(true)} title="Team & Assignments">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M1 13c0-2.2 2.2-4 5-4s5 1.8 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="5" r="2" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M15 12.5c0-1.7-1.3-3-3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          {isOwner && <AnnotatorPowerControl />}
          <ThemeToggle />
          {isOwner && <AnalyzeNewFilesButton />}
          {isOwner && <PublishButton />}
          <PipelineStatus phase={pipelineState?.phase} progress={pipelineState?.progress} />
          {isOwner && onNewProject && (
            <button
              className="btn-dashboard-icon"
              onClick={onNewProject}
              title="Back to dashboard"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor"/>
                <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor"/>
                <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor"/>
                <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor"/>
              </svg>
            </button>
          )}
        </div>
      </header>
      {pipelineState && (
        <div className={`pipeline-strip phase-${pipelineState.phase}`}>
          <div
            className="pipeline-strip-fill"
            style={{ width: `${pipelineState.progress}%` }}
          />
        </div>
      )}
      {isOwner && <IncrementalBanner />}
      {showTeam && <TeamPanel onClose={() => setShowTeam(false)} />}
    </>
  );
}

// Monco project number chip — parsed from the project's S3 URI
// (.../smart-surveyor/projects/<id>/...), so the user always knows which
// app.monco.ai project they're looking at. Click to copy.
function ProjectIdBadge() {
  const project = useProjectStore((s) => s.project);
  const setToast = useProjectStore((s) => s.setToast);
  const m = project?.s3_uri?.match(/\/projects\/([^/]+)\/?/);
  const moncoId = m?.[1];
  if (!moncoId) return null;
  const copy = () => {
    navigator.clipboard?.writeText(moncoId).then(
      () => setToast(`Project id ${moncoId} copied`, 'success'),
      () => undefined,
    );
  };
  return (
    <span
      className="project-id-badge"
      onClick={copy}
      title={`Monco project ${moncoId}\n${project?.s3_uri || ''}\ninternal: ${project?.project_id || ''}\n(click to copy)`}
    >
      #{moncoId}
    </span>
  );
}

function PipelineStatus({ phase, progress }: { phase?: PipelinePhase; progress?: number }) {
  if (!phase) return null;
  const labels: Record<string, string> = {
    research: 'Research',
    waiting: 'Paused',
    extraction: 'Extraction',
    complete: 'Complete',
  };
  return (
    <span className={`pipeline-status-badge phase-${phase}`}>
      {labels[phase]} {progress != null && phase !== 'complete' ? `${progress}%` : ''}
    </span>
  );
}

function AnalyzeNewFilesButton() {
  const project = useProjectStore((s) => s.project);
  const setToast = useProjectStore((s) => s.setToast);
  const refreshedAt = useProjectStore((s) => s.incrementalAnalysisRefreshedAt);
  const s3Uri = project?.s3_uri;
  const projectId = project?.project_id;
  const [newFiles, setNewFiles] = useState<string[]>([]);
  const [analyzing, setAnalyzing] = useState(false);

  const refreshNewFiles = useCallback(async () => {
    try {
      const response = await api.getProjectNewFiles();
      setNewFiles(response?.new_files ?? []);
    } catch {
      setNewFiles([]);
    }
  }, []);

  const onClick = useCallback(async () => {
    if (analyzing || !s3Uri) return;
    const fileCount = newFiles.length;
    const confirmed = window.confirm(
      `${fileCount} new file${fileCount === 1 ? '' : 's'} detected:\n\n${newFiles.join('\n')}\n\n` +
      `Analyze just these new files and merge into this project? ` +
      `Existing annotations and extractions are kept — nothing is deleted.`
    );
    if (!confirmed) return;
    // Show the loader in place instead of a floating toast.
    setAnalyzing(true);
    try {
      const response = await api.analyzeNewFiles(s3Uri);
      if (!response) throw new Error('Failed to start analysis');
    } catch {
      setAnalyzing(false);
      setToast('Failed to start analysis', 'error');
    }
  }, [analyzing, s3Uri, newFiles, setToast]);

  // Read the live pipeline status so "Analyzing…" survives a page refresh.
  useEffect(() => {
    let cancelled = false;
    if (!s3Uri || !projectId) {
      setNewFiles([]);
      setAnalyzing(false);
      return;
    }
    (async () => {
      const [newFilesResponse, pipelineStatus] = await Promise.all([
        api.getProjectNewFiles().catch(() => null),
        api.getPipelineStatus(projectId).catch(() => null),
      ]);
      if (cancelled) return;
      setNewFiles(newFilesResponse?.new_files ?? []);
      setAnalyzing(!!pipelineStatus?.running);
    })();
    return () => { cancelled = true; };
  }, [s3Uri, projectId, refreshedAt]);

  // When the run finishes (set after the S3 upload), re-fetch so the button hides.
  useEffect(() => {
    if (!analyzing || !projectId) return;
    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      const pipelineStatus = await api.getPipelineStatus(projectId).catch(() => null);
      if (cancelled || !pipelineStatus) return;
      if (!pipelineStatus.running) {
        setAnalyzing(false);
        refreshNewFiles();
      }
    }, 3000);
    return () => { cancelled = true; window.clearInterval(intervalId); };
  }, [analyzing, projectId, refreshNewFiles]);

  if (!s3Uri || (!analyzing && newFiles.length === 0)) return null;

  return (
    <button
      className="btn-analyze-new"
      onClick={onClick}
      disabled={analyzing}
      title={analyzing
        ? 'Analyzing new files…'
        : `Analyze new files (merged, non-destructive):\n${newFiles.join('\n')}`}
    >
      {analyzing ? (
        <><span className="btn-spinner" aria-hidden="true" />Analyzing…</>
      ) : (
        `Analyze ${newFiles.length} new file${newFiles.length === 1 ? '' : 's'}`
      )}
    </button>
  );
}

function IncrementalBanner() {
  const project = useProjectStore((s) => s.project);
  const refreshedAt = useProjectStore((s) => s.incrementalAnalysisRefreshedAt);
  const pid = project?.project_id;
  const [pending, setPending] = useState<string[]>([]);

  const dismiss = useCallback(async () => {
    try {
      await api.clearIncrementalPending();
    } catch {
      /* ignore */
    }
    setPending([]);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!pid) {
      setPending([]);
      return;
    }
    api.getIncrementalStatus()
      .then((r) => { if (!cancelled) setPending(r?.pending_actions ?? []); })
      .catch(() => { if (!cancelled) setPending([]); });
    return () => { cancelled = true; };
  }, [pid, refreshedAt]);

  if (!pending.includes('rerun_lv_matching')) return null;

  return (
    <div className="incremental-banner">
      <span>
        New LV positions were added. Floor-plan LV matches may be out of date —
        re-run matching in the Review tab (your manual match overrides are preserved).
      </span>
      <button className="btn-dismiss" onClick={dismiss}>Dismiss</button>
    </div>
  );
}

function PublishButton() {
  const report = useProjectStore((s) => s.researchReport);
  const [publishing, setPublishing] = useState(false);
  // Full publish result (including any partial-failure list) rather than
  // just a label string — lets us render a warning badge + a popover
  // itemizing what failed, so a half-published bucket doesn't get silently
  // declared successful.
  const [publishResult, setPublishResult] = useState<api.PublishResult | null>(null);
  // `errorMessage` is for transport-level failures (network down, 500
  // from the server, request rejected) — distinct from a 200-with-
  // `status: "partial"`. Both clear after 5s of inactivity.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showFailures, setShowFailures] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const publishProgress = useSSEStore((s) => s.skillProgress['publish']);

  if (!report) return null;

  const handlePublish = async () => {
    setPublishing(true);
    setPublishResult(null);
    setErrorMessage(null);
    setShowFailures(false);
    setStartTime(Date.now());
    useSSEStore.getState().clearSkillProgress('publish');
    try {
      const result = await api.publish();
      if (result) {
        setPublishResult(result);
        // Auto-open the failures list when partial so the user sees
        // what's missing without hunting for a button.
        if (result.status === 'partial' && result.failures && result.failures.length > 0) {
          setShowFailures(true);
          // Surface the partial publish to Sentry as a warning. Each
          // individual upload failure is already captured server-side
          // with publish_kind / publish_key tags; this client-side
          // event correlates the user-visible "publish ended partial"
          // moment with the server events, so a Sentry replay shows
          // exactly which click produced the half-published bucket.
          try {
            Sentry.withScope((scope) => {
              scope.setLevel('warning');
              scope.setTag('publish_status', 'partial');
              scope.setTag('publish_location', result.location);
              scope.setContext('publish_result', {
                failures_count: result.failures?.length ?? 0,
                artifacts_written: result.artifacts_written,
                artifacts_total: result.artifacts_total,
                location: result.location,
              });
              scope.setExtra('failures', result.failures ?? []);
              Sentry.captureMessage(
                `Publish partial — ${result.failures?.length ?? 0} artifact(s) failed`,
              );
            });
          } catch {
            /* sentry not initialised */
          }
        } else {
          // Auto-clear the green "Published" state after 5s; keep the
          // partial state visible until the user dismisses it.
          setTimeout(() => setPublishResult(null), 5000);
        }
      } else {
        setErrorMessage('Publish failed');
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Publish failed');
      setTimeout(() => setErrorMessage(null), 5000);
      // Network / 5xx — capture so Sentry sees both transport failures
      // and partial-success cases. captureException keeps the original
      // stack trace.
      try {
        Sentry.captureException(err);
      } catch { /* sentry not initialised */ }
    }
    setPublishing(false);
    setStartTime(null);
    useSSEStore.getState().clearSkillProgress('publish');
  };

  const hasProgress = publishing && publishProgress && publishProgress.total > 0;
  const pct = hasProgress ? Math.round((publishProgress.processed / publishProgress.total) * 100) : 0;
  const isPartial = publishResult?.status === 'partial';
  const failures = publishResult?.failures ?? [];

  let label = 'Publish Research';
  if (hasProgress) {
    label = `${publishProgress.processed}/${publishProgress.total}`;
  } else if (publishing) {
    label = 'Publishing...';
  } else if (errorMessage) {
    label = errorMessage;
  } else if (publishResult) {
    if (publishResult.status === 'published') {
      label = `Published (${publishResult.artifacts_written} artifacts)`;
    } else {
      // Show the count so the user knows at a glance how bad it is.
      label = `Partial — ${failures.length} failed`;
    }
  }

  // ETA calculation
  let eta = '';
  if (hasProgress && startTime && publishProgress.processed > 0) {
    const elapsed = (Date.now() - startTime) / 1000;
    const rate = publishProgress.processed / elapsed;
    const remaining = (publishProgress.total - publishProgress.processed) / rate;
    if (remaining < 60) {
      eta = `~${Math.ceil(remaining)}s left`;
    } else {
      eta = `~${Math.ceil(remaining / 60)}m left`;
    }
  }

  return (
    <div className="publish-button-wrapper" style={{ position: 'relative' }}>
      <button
        className="btn-publish-header"
        onClick={handlePublish}
        disabled={publishing}
        title={hasProgress && publishProgress.detail ? publishProgress.detail : undefined}
        // Partial state gets an amber-ish tint via inline style so we
        // don't have to touch the shared .btn-publish-header CSS (which
        // is used elsewhere). Failure list lives in a sibling popover
        // below, so this stays simple.
        style={isPartial ? { background: '#b45309', borderColor: '#92400e' } : undefined}
      >
        {label}
        {eta && <span className="publish-eta"> {eta}</span>}
      </button>
      {isPartial && (
        <button
          className="btn-publish-header"
          style={{
            marginLeft: 4,
            padding: '4px 8px',
            fontSize: 12,
            background: 'transparent',
            border: '1px solid #b45309',
            color: '#b45309',
          }}
          onClick={() => setShowFailures((v) => !v)}
          title="Show / hide failures"
        >
          {showFailures ? 'Hide' : `Details (${failures.length})`}
        </button>
      )}
      {hasProgress && (
        <div className="publish-header-bar-bg">
          <div className="publish-header-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
      {showFailures && publishResult && (
        <PublishFailurePopover
          result={publishResult}
          onRetry={handlePublish}
          onDismiss={() => {
            setShowFailures(false);
            setPublishResult(null);
          }}
          retrying={publishing}
        />
      )}
    </div>
  );
}

/**
 * Popover that itemizes per-artifact failures from a partial publish and
 * offers a "Retry publish" button. Retry just calls /api/publish again —
 * the backend's per-step uploads overwrite existing objects, so a retry
 * re-uploads everything (including the parts that already succeeded);
 * that's safe because S3 PutObject is idempotent.
 */
function PublishFailurePopover({
  result,
  onRetry,
  onDismiss,
  retrying,
}: {
  result: api.PublishResult;
  onRetry: () => void;
  onDismiss: () => void;
  retrying: boolean;
}) {
  const failures = result.failures ?? [];
  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 6,
        width: 360,
        maxHeight: 320,
        overflowY: 'auto',
        background: 'var(--bg-elev, #1f2530)',
        border: '1px solid var(--border, #3a3d44)',
        borderRadius: 6,
        padding: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        zIndex: 100,
        fontSize: 12,
        color: 'var(--fg, #e8e8e8)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: '1px solid var(--border, #3a3d44)',
        }}
      >
        <strong style={{ color: '#fbbf24' }}>
          Partial publish — {failures.length} artifact{failures.length === 1 ? '' : 's'} failed
        </strong>
        <button
          onClick={onDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
          }}
          title="Dismiss"
        >
          ×
        </button>
      </div>
      <div style={{ marginBottom: 8, color: 'var(--text-muted, #9a9da3)' }}>
        Uploaded to: <code style={{ fontSize: 11 }}>{result.location}</code>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {failures.map((f, i) => (
          <li
            key={`${f.key}-${i}`}
            style={{
              padding: '6px 0',
              borderBottom: i < failures.length - 1 ? '1px solid var(--border-faint, #2a2d34)' : 'none',
            }}
          >
            <div style={{ fontWeight: 600 }}>{f.key}</div>
            <div style={{ color: 'var(--text-muted, #9a9da3)', fontSize: 11 }}>
              [{f.kind}] {f.error}
            </div>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
        <button
          className="btn-publish-header"
          style={{ flex: 1, padding: '6px 10px', fontSize: 12 }}
          onClick={onRetry}
          disabled={retrying}
          title="Re-runs /api/publish; existing artifacts are overwritten so missing ones get filled in"
        >
          {retrying ? 'Retrying...' : 'Retry publish'}
        </button>
      </div>
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() =>
    document.documentElement.getAttribute('data-theme') || 'dark'
  );

  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('monco-theme', next);
    setTheme(next);
  }, [theme]);

  return (
    <button
      className="btn-theme-toggle"
      onClick={toggle}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {theme === 'dark' ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.4 3.4l.7.7M11.9 11.9l.7.7M12.6 3.4l-.7.7M4.1 11.9l-.7.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M13.4 10.6A6 6 0 015.4 2.6a6 6 0 108 8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      )}
    </button>
  );
}

// ── Annotator (annotator.monco.ai) power control — owner-only ──
// Starts/stops the annotator EC2 box from the platform so the larger box
// only runs during work hours. Polls status; auto-refreshes while the
// instance is transitioning (pending/stopping).
function fmtAgo(s: number): string {
  if (s < 90) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function AnnotatorPowerControl() {
  const [status, setStatus] = useState<api.AnnotatorStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [showUsers, setShowUsers] = useState(false);

  const refresh = useCallback(async () => {
    const s = await api.annotatorStatus();
    if (s) setStatus(s);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll faster while transitioning, slower when settled.
  useEffect(() => {
    const transitioning = status?.state === 'pending' || status?.state === 'stopping' || status?.state === 'shutting-down';
    const interval = transitioning ? 5000 : 30000;
    const id = setInterval(refresh, interval);
    return () => clearInterval(id);
  }, [status?.state, refresh]);

  const state = status?.state;
  const isRunning = state === 'running';
  const isStopped = state === 'stopped';
  const isTransitioning = state === 'pending' || state === 'stopping' || state === 'shutting-down';

  const active5 = status?.activity?.active_5min ?? 0;
  const lastAgo = status?.activity?.last_activity_seconds_ago;

  const handleToggle = async () => {
    if (busy || isTransitioning || !state) return;
    if (isRunning) {
      const warn = active5 > 0
        ? `⚠️ ${active5} annotator${active5 > 1 ? 's are' : ' is'} actively working right now. Stop anyway? They'll be disconnected (work autosaves) and it takes ~1-2 min to start back up.`
        : 'Stop the annotator server? Nobody is actively working right now. It takes ~1-2 min to start back up.';
      if (!confirm(warn)) return;
      setBusy(true);
      try {
        const r = await api.annotatorStop();
        if (r) { setStatus((s) => s ? { ...s, state: 'stopping' } : s); }
      } finally { setBusy(false); refresh(); }
    } else if (isStopped) {
      setBusy(true);
      try {
        const r = await api.annotatorStart();
        if (r) { setStatus((s) => s ? { ...s, state: 'pending' } : s); }
      } finally { setBusy(false); refresh(); }
    }
  };

  if (!status) return null;

  const dot = isRunning ? '#10b981' : isStopped ? '#6b7280' : '#f59e0b';
  const label = isRunning ? 'Annotator on'
    : isStopped ? 'Annotator off'
    : state === 'pending' ? 'Starting…'
    : 'Stopping…';
  const actionLabel = isRunning ? 'Stop' : isStopped ? 'Start' : '…';

  // Human "active" sub-label when the box is running.
  let activeText: string | null = null;
  if (isRunning && status.activity) {
    if (active5 > 0) activeText = `${active5} working`;
    else if (lastAgo != null && lastAgo < 3600) activeText = `idle ${lastAgo < 90 ? lastAgo + 's' : Math.round(lastAgo / 60) + 'm'}`;
    else activeText = 'idle';
  }

  const users = status.activity?.users ?? null;
  const hasUsers = !!users && users.length > 0;

  return (
    <div
      className="annotator-power"
      title={`annotator.monco.ai — ${state}${status.instance_type ? ' (' + status.instance_type + ')' : ''}${
        status.activity ? ` · ${active5} active (5m), ${status.activity.active_15min} (15m)` : ''
      }`}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '4px 8px', borderRadius: 6,
        border: '1px solid var(--border, #334155)',
        fontSize: 12,
      }}
    >
      <span style={{
        width: 8, height: 8, borderRadius: 4, background: dot,
        boxShadow: isTransitioning ? `0 0 6px ${dot}` : 'none',
        animation: isTransitioning ? 'pulse 1.4s ease-in-out infinite' : 'none',
      }} />
      <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
      {activeText && (
        <span
          onClick={hasUsers ? () => setShowUsers((v) => !v) : undefined}
          title={hasUsers ? 'Show who is online' : undefined}
          style={{
            whiteSpace: 'nowrap', fontSize: 11,
            padding: '1px 6px', borderRadius: 8,
            background: active5 > 0 ? 'rgba(16,185,129,0.18)' : 'rgba(148,163,184,0.15)',
            color: active5 > 0 ? '#10b981' : '#94a3b8',
            fontWeight: active5 > 0 ? 600 : 400,
            cursor: hasUsers ? 'pointer' : 'default',
          }}
        >
          {active5 > 0 ? '● ' : ''}{activeText}{hasUsers ? (showUsers ? ' ▴' : ' ▾') : ''}
        </span>
      )}
      <button
        onClick={handleToggle}
        disabled={busy || isTransitioning}
        style={{
          padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
          cursor: (busy || isTransitioning) ? 'default' : 'pointer',
          border: 'none',
          background: isRunning ? '#dc2626' : isStopped ? '#10b981' : '#475569',
          color: '#fff', opacity: (busy || isTransitioning) ? 0.5 : 1,
        }}
      >
        {actionLabel}
      </button>
      {showUsers && hasUsers && (
        <div
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
            minWidth: 220, padding: '8px 10px',
            background: 'var(--panel, #0f172a)',
            border: '1px solid var(--border, #334155)', borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          }}
        >
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 6 }}>
            Online ({users!.length}) · last 15 min
          </div>
          {users!.map((u) => {
            const fresh = u.seconds_ago <= 300;
            return (
              <div key={u.username} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '2px 0' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: fresh ? '#10b981' : '#94a3b8', flexShrink: 0 }} />
                  <span style={{ color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.username}</span>
                </span>
                <span style={{ color: '#94a3b8', fontSize: 11, flexShrink: 0 }}>{fmtAgo(u.seconds_ago)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
